import { Checkbox, IconButton, Link, MenuItem, TableRow } from "@mui/material";
import { GridMoreVertIcon } from "@mui/x-data-grid-pro";
import { useTranslation } from "react-i18next";
import { useContext, useState } from "react";
import { format } from "date-fns";

import { StyledTableCell } from "../../../../../common/components/functional/simpleTable/styled";
import {
  SelectLightEllipses,
  TextFieldLight,
} from "../../../../../common/components/styled/styled";
import { ActionMenu } from "./ActionMenu";
import {
  ManualSalary,
  ManualSalaryRepeatType,
} from "../../../../talents/talentAPI";
import { leadingZerosDateFormat } from "../../../../../common/helpers/formatDateTime";
import { useAppSelector } from "../../../../../app/hooks";
import { ExpenseTypeOption, Option } from "../../ManualSalaries/ManualSalaries";
import { AccessRightContext } from "../../../../user/AccessRightProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import CommonTextField from "../../../../../common/components/functional/commonTextField/CommonTextField";
import Tooltip from "@mui/material/Tooltip";

interface Props {
  manualSalary: ManualSalary;
  eezyCompanyName: string;
  index: number;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  onCopy: (index: number) => void;
  assignmentOptions: Option[];
  ccOptions: Option[];
  eezyCcOptions: Option[];
  expenseTypeOptions: ExpenseTypeOption[];
  openPayslip: (payslipId: string) => void;
}

export const ManualSalaryItem = ({
  manualSalary,
  eezyCompanyName,
  index,
  onEdit,
  onDelete,
  onCopy,
  assignmentOptions,
  ccOptions,
  eezyCcOptions,
  expenseTypeOptions,
  openPayslip,
}: Props) => {
  const { t } = useTranslation();
  const expenseTypeTranslations = useAppSelector(
    (state) => state.localized.expenseType,
  );
  const { canEditManualSalaries } = useContext(AccessRightContext);

  const [actionMenuAnchor, setActionMenuAnchor] = useState<{
    target: null | HTMLElement;
    id: string;
  }>({ target: null, id: "" });

  const groupName =
    expenseTypeOptions.find(
      (option) => option.value === manualSalary.expenseTypeCode,
    )?.group || "";

  const unitTranslations = useAppSelector((state) => state.localized.unit);

  const translatedExpenseTypeCode =
    (expenseTypeTranslations &&
      expenseTypeTranslations[manualSalary.expenseTypeCode]) ||
    manualSalary.expenseTypeCode;

  const getSelectedOptionLabelById = (
    key: string,
    assignmentId: string | null,
  ) => {
    const options: Record<string, Option[]> = {
      assignments: assignmentOptions,
      costCenters: ccOptions,
      eezyCostCenters: eezyCcOptions,
    };

    const selected = options[key]?.find(
      (option) => option.value === assignmentId,
    );
    return selected ? selected.label : "";
  };

  return (
    <TableRow>
      <StyledTableCell sx={{ maxWidth: 20 }}>
        <Tooltip title={groupName}>
          <SelectLightEllipses
            fullWidth
            disabled={true}
            size="small"
            value={groupName}
          >
            <MenuItem value={groupName}>{groupName}</MenuItem>
          </SelectLightEllipses>
        </Tooltip>
      </StyledTableCell>
      <StyledTableCell sx={{ maxWidth: 20 }}>
        <Tooltip title={translatedExpenseTypeCode}>
          <SelectLightEllipses
            fullWidth
            disabled={true}
            size="small"
            value={manualSalary.expenseTypeCode}
          >
            <MenuItem value={manualSalary.expenseTypeCode}>
              {translatedExpenseTypeCode}
            </MenuItem>
          </SelectLightEllipses>
        </Tooltip>
      </StyledTableCell>
      <StyledTableCell sx={{ maxWidth: 20 }}>
        <TextFieldLight
          fullWidth
          disabled={true}
          size="small"
          value={manualSalary.description}
        />
      </StyledTableCell>
      <StyledTableCell sx={{ minWidth: 90 }}>
        <TextFieldLight
          fullWidth
          disabled={true}
          size="small"
          value={format(new Date(manualSalary.date), leadingZerosDateFormat)}
        />
      </StyledTableCell>
      <StyledTableCell>
        <TextFieldLight
          fullWidth
          disabled={true}
          size="small"
          value={manualSalary.amount}
        />
      </StyledTableCell>
      <StyledTableCell>
        <TextFieldLight
          fullWidth
          disabled={true}
          size="small"
          value={manualSalary.unitCost}
        />
      </StyledTableCell>
      <StyledTableCell>
        <TextFieldLight
          fullWidth
          disabled={true}
          size="small"
          value={manualSalary.unit && unitTranslations[manualSalary.unit]}
        />
      </StyledTableCell>
      <StyledTableCell
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "54px",
        }}
      >
        {!!manualSalary.payslipId ? (
          <Link
            onClick={() => openPayslip(manualSalary.payslipId!!)}
            style={{ cursor: "pointer" }}
          >
            {t("open")}
          </Link>
        ) : (
          <Link onClick={() => {}} style={{ color: "grey" }}>
            {t("open")}
          </Link>
        )}
      </StyledTableCell>
      <StyledTableCell>
        <TextFieldLight
          fullWidth
          disabled
          size="small"
          value={t(`talentProfile:salaryItem_${manualSalary.repeatType}`)}
        />
      </StyledTableCell>
      <StyledTableCell sx={{ minWidth: 90 }}>
        {manualSalary.repeatUntil ? (
          <DesktopDatePicker
            disableOpenPicker
            disabled
            value={
              manualSalary.repeatUntil
                ? new Date(manualSalary.repeatUntil)
                : null
            }
            slots={{ textField: TextFieldLight }}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                sx: { input: { textAlign: "center" } },
              },
            }}
          />
        ) : (
          <CommonTextField
            fullWidth
            disabled
            value={
              manualSalary.repeatType === ManualSalaryRepeatType.NONE
                ? ""
                : t("talentProfile:untilFurtherNotice")
            }
          />
        )}
      </StyledTableCell>
      <StyledTableCell sx={{ maxWidth: 20 }}>
        <Tooltip
          title={getSelectedOptionLabelById(
            "assignments",
            manualSalary.assignmentId,
          )}
        >
          <SelectLightEllipses
            fullWidth
            disabled
            size="small"
            value={manualSalary.assignmentId || ""}
            displayEmpty
          >
            <MenuItem value="">{eezyCompanyName}</MenuItem>
            {assignmentOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </SelectLightEllipses>
        </Tooltip>
      </StyledTableCell>
      <StyledTableCell sx={{ maxWidth: 20 }}>
        {manualSalary.assignmentId ? (
          <Tooltip
            title={getSelectedOptionLabelById(
              "costCenters",
              manualSalary.customerCostCenterId,
            )}
          >
            <SelectLightEllipses
              fullWidth
              disabled={true}
              size="small"
              value={manualSalary.customerCostCenterId}
            >
              {ccOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </SelectLightEllipses>
          </Tooltip>
        ) : (
          <Tooltip
            title={getSelectedOptionLabelById(
              "eezyCostCenters",
              manualSalary.eezyCostCenterId,
            )}
          >
            <SelectLightEllipses
              fullWidth
              disabled={true}
              size="small"
              value={manualSalary.eezyCostCenterId}
            >
              {eezyCcOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </SelectLightEllipses>
          </Tooltip>
        )}
      </StyledTableCell>
      <StyledTableCell align="center" sx={{ maxWidth: 20 }}>
        <Checkbox disabled={true} checked={manualSalary.isBillable} />
      </StyledTableCell>
      <StyledTableCell>
        {canEditManualSalaries && (
          <IconButton
            onClick={(e) =>
              setActionMenuAnchor({ target: e.currentTarget, id: "" })
            }
          >
            <GridMoreVertIcon />
          </IconButton>
        )}
      </StyledTableCell>
      <ActionMenu
        open={!!actionMenuAnchor.target}
        anchorEl={actionMenuAnchor}
        onClose={() => setActionMenuAnchor({ target: null, id: "" })}
        index={index}
        isRowEditable={true}
        onDeleteRow={() => onDelete(index)}
        onCopyRow={() => onCopy(index)}
        onEdit={() => onEdit(index)}
      />
    </TableRow>
  );
};
